import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  cacheSvg,
  getCachedSvg,
  postSvgCacheToQbQuestion,
  postSvgCacheToServer,
  subscribeCacheUpdate,
} from '../../utils/tikzSvgCache';
import { renderTikzToSvg } from '../../utils/tikzRender';

const RENDER_TIMEOUT_MS = 25000;

// Strip control chars the same way the server's sanitize_tikz_code keeps
// before hashing. Keeps the hash parity for the write-back endpoint.
const sanitizeTikzForRender = (value = '') =>
  String(value).replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');

const STATUS = {
  IDLE: 'idle',
  RENDERING: 'rendering',
  SUCCESS: 'success',
  ERROR: 'error',
};

/**
 * Lazy TikZ → SVG tile.
 *
 * Renders cached SVG inline when available. Otherwise either auto-renders
 * (if `autoRender`) or shows a "View Diagram" button. Each render runs in
 * an isolated iframe via renderTikzToSvg; we only see the resulting SVG
 * string and inline it, so a failed iframe never pollutes the parent DOM.
 */
const DiagramTile = ({
  diagram,
  worksheetId,
  qbQuestionId,
  autoRender = false,
  className = '',
  onSvgRendered,
}) => {
  const rawCode = useMemo(() => {
    if (!diagram) return '';
    const code = typeof diagram === 'string' ? diagram : diagram.code;
    return typeof code === 'string' ? code : '';
  }, [diagram]);

  const sanitizedCode = useMemo(() => sanitizeTikzForRender(rawCode), [rawCode]);

  const initialServerSvg = useMemo(() => {
    if (!diagram || typeof diagram !== 'object') return '';
    const svg = diagram.svg_cache;
    return typeof svg === 'string' && svg.trim() ? svg : '';
  }, [diagram]);

  // Cache lookups are pure of React state, so changes from the background
  // prerender don't trigger this useMemo. cacheTick is bumped by the
  // subscribe-to-cache-updates effect below to force a re-check whenever
  // someone else (background queue, sibling tile) writes our SVG.
  const [cacheTick, setCacheTick] = useState(0);

  const initialLocalSvg = useMemo(() => {
    if (initialServerSvg) return '';
    if (!sanitizedCode) return '';
    return getCachedSvg(sanitizedCode) || '';
  }, [initialServerSvg, sanitizedCode, cacheTick]);

  const cachedSvg = initialServerSvg || initialLocalSvg;

  const [status, setStatus] = useState(() => {
    if (cachedSvg) return STATUS.SUCCESS;
    if (autoRender) return STATUS.RENDERING;
    return STATUS.IDLE;
  });
  const [renderedSvg, setRenderedSvg] = useState(cachedSvg || '');
  const [errorMessage, setErrorMessage] = useState('');

  const renderRunIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  // If a parent updates the diagram (e.g., the cache arrives via a worksheet
  // reload), pick up the new SVG without forcing a re-mount.
  useEffect(() => {
    if (cachedSvg && cachedSvg !== renderedSvg) {
      setRenderedSvg(cachedSvg);
      setStatus(STATUS.SUCCESS);
    }
  }, [cachedSvg, renderedSvg]);

  // Listen for background-prerender cache writes targeting our diagram so
  // an idle/rendering tile can flip to the cached SVG without spinning up
  // another iframe.
  useEffect(() => {
    if (!sanitizedCode) return undefined;
    const unsubscribe = subscribeCacheUpdate((updatedCode) => {
      if (updatedCode !== sanitizedCode) return;
      setCacheTick((tick) => tick + 1);
    });
    return unsubscribe;
  }, [sanitizedCode]);

  const handleRenderSuccess = useCallback(
    (svgHtml) => {
      setRenderedSvg(svgHtml);
      setStatus(STATUS.SUCCESS);
      setErrorMessage('');
      if (sanitizedCode) {
        cacheSvg(sanitizedCode, svgHtml);
        if (worksheetId) {
          postSvgCacheToServer(worksheetId, [{ tikzCode: sanitizedCode, svgHtml }]);
        }
        if (qbQuestionId) {
          postSvgCacheToQbQuestion(qbQuestionId, { tikzCode: sanitizedCode, svgHtml });
        }
      }
      if (typeof onSvgRendered === 'function') {
        try {
          onSvgRendered(svgHtml);
        } catch (_error) {
          // listener errors are not the tile's concern
        }
      }
    },
    [sanitizedCode, worksheetId, qbQuestionId, onSvgRendered],
  );

  useEffect(() => {
    if (status !== STATUS.RENDERING) return undefined;
    if (!sanitizedCode) {
      setStatus(STATUS.ERROR);
      setErrorMessage('Diagram source is empty');
      return undefined;
    }

    // The cache may have been populated since we entered the RENDERING
    // state (e.g., background prerender finished while we were mounting).
    // Skip the iframe entirely if so.
    const preCached = getCachedSvg(sanitizedCode);
    if (preCached) {
      handleRenderSuccess(preCached);
      return undefined;
    }

    const runId = renderRunIdRef.current + 1;
    renderRunIdRef.current = runId;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    renderTikzToSvg(sanitizedCode, {
      timeoutMs: RENDER_TIMEOUT_MS,
      signal: controller.signal,
    })
      .then((svgHtml) => {
        if (renderRunIdRef.current !== runId) return;
        if (!svgHtml) {
          setStatus(STATUS.ERROR);
          setErrorMessage('Render produced empty SVG');
          return;
        }
        handleRenderSuccess(svgHtml);
      })
      .catch((err) => {
        if (renderRunIdRef.current !== runId) return;
        if (controller.signal.aborted) return;
        setStatus(STATUS.ERROR);
        setErrorMessage(err?.message || 'Render failed');
      });

    return () => {
      // Aborting tears down the iframe (and its TikZJax WASM heap)
      // immediately rather than waiting for the 25s outer timeout.
      try { controller.abort(); } catch (_e) { /* ignore */ }
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    };
  }, [status, sanitizedCode, handleRenderSuccess]);

  // Final unmount safety: ensure any controller still in flight is aborted
  // when the component is torn down, even if React batches state changes
  // such that the rendering effect's cleanup ran with a stale controller.
  useEffect(() => () => {
    const controller = abortControllerRef.current;
    if (controller) {
      try { controller.abort(); } catch (_e) { /* ignore */ }
      abortControllerRef.current = null;
    }
  }, []);

  const handleViewClick = useCallback(() => {
    setErrorMessage('');
    setStatus(STATUS.RENDERING);
  }, []);

  const baseClass = `diagram-tile ${className}`.trim();

  if (!sanitizedCode && !renderedSvg) {
    return (
      <div className={`${baseClass} text-xs text-gray-500 italic`}>
        (No diagram source)
      </div>
    );
  }

  if (status === STATUS.SUCCESS && renderedSvg) {
    return (
      <div className={`${baseClass} diagram-tile--rendered w-full overflow-auto`}>
        <div
          className="diagram-svg-box mx-auto max-w-full"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderedSvg }}
        />
      </div>
    );
  }

  if (status === STATUS.RENDERING) {
    return (
      <div
        className={`${baseClass} diagram-tile--rendering inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm`}
        role="status"
        aria-live="polite"
      >
        <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
        <span>Rendering diagram…</span>
      </div>
    );
  }

  if (status === STATUS.ERROR) {
    return (
      <div
        className={`${baseClass} diagram-tile--error inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm`}
        role="alert"
      >
        <span>Diagram failed to render{errorMessage ? `: ${errorMessage}` : ''}.</span>
        <button
          type="button"
          onClick={handleViewClick}
          className="ml-1 underline font-medium hover:text-red-900"
        >
          Retry
        </button>
      </div>
    );
  }

  // IDLE
  return (
    <div
      className={`${baseClass} diagram-tile--placeholder inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-dashed border-blue-300 text-blue-800 text-sm`}
    >
      <span aria-hidden="true">📐</span>
      <span>Diagram available</span>
      <button
        type="button"
        onClick={handleViewClick}
        className="ml-1 px-2 py-0.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        View Diagram
      </button>
    </div>
  );
};

export default DiagramTile;
